/**
 * ADM-27 科目管理页（subject-course-sku:SC-01~SC-16 + SC-50~SC-52 满分扩展 前端）。
 *
 * 筛选区：名称/编码输入 + 状态下拉 + 查询/重置。
 * 列表表格：科目编码、科目名称、满分、关联 SKU 数、状态标签、更新时间、操作（编辑/停用·启用/查看关联SKU）。
 * 新建/编辑：统一对话框表单（编码新建可填/编辑禁用、名称、满分 1~1000 整数默认 100、
 * 分数段编辑器（subject-score-bands-and-scores：默认派生/自定义边界，SC-53/SC-54）；编辑携带 version）。
 * 停用/启用：Popconfirm 二次确认。
 * 调低满分遇超限分数记录：409 捕获 affectedCount → Modal.confirm → confirm: true 重提（SC-55/SC-56）。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { EditDialog } from "../../components/EditDialog";
import { useDraggableModal } from "../../components/useDraggableModal";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import {
  subjectsApi,
  type CreateSubject,
  type SubjectItem,
  type SubjectListQuery,
  type SubjectStatus,
  type UpdateSubject,
} from "../../api/subjects.api";
import { subjectStatusLabel } from "../../api/labels";
import { ApiError } from "../../api/transport";
import { ScoreBandEditor } from "./ScoreBandEditor";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";

const { Title } = Typography;

/** 科目状态标签。 */
function SubjectStatusTag({ status }: { status: SubjectStatus }) {
  const color = status === "ACTIVE" ? "green" : "default";
  return <Tag color={color}>{subjectStatusLabel[status]}</Tag>;
}

/** 提取错误信息文案。 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function SubjectsPage() {
  const navigate = useNavigate();
  // 命令式确认弹窗（409 截断二次确认）统一走可拖动包装
  const modal = useDraggableModal();
  const [data, setData] = useState<SubjectItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<SubjectListQuery>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SubjectItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateSubject & { version?: number }>();
  /** 表单内实时满分（边界校验上限与预览切分基准随输入更新）。 */
  const watchedFullScore = Form.useWatch("fullScore", form) as
    number | undefined;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await subjectsApi.listSubjects({
        ...filters,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setDialogOpen(true);
  };

  const openEdit = (record: SubjectItem) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      fullScore: record.fullScore,
      // null（默认派生）在表单内以 undefined 表达（antd 表单值不支持 null），提交时 ?? null 还原
      scoreBandEdges: record.scoreBandEdges ?? undefined,
      version: record.version,
    });
    setDialogOpen(true);
  };

  /**
   * 提交（confirmTruncate = 用户已在 409 确认框确认截断后重提）。
   * 调低满分遇超限分数记录：服务端 409 信封携带 affectedCount，Modal.confirm
   * 二次确认后携带 confirm: true 同事务截断（SC-55/SC-56）。
   */
  const handleSubmit = async (confirmTruncate = false) => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editing) {
        const update: UpdateSubject = {
          name: values.name,
          fullScore: values.fullScore,
          scoreBandEdges: values.scoreBandEdges ?? null,
          ...(confirmTruncate ? { confirm: true } : {}),
          version: values.version ?? editing.version,
        };
        try {
          await subjectsApi.updateSubject(editing.id, update);
        } catch (err) {
          if (
            err instanceof ApiError &&
            err.code === "product-catalog.subject_full_score_lower_blocked" &&
            !confirmTruncate
          ) {
            setSubmitting(false);
            modal.confirm({
              title: "调低科目满分确认",
              content: `${err.message}。确认继续将把超出新满分的分数记录截断为新满分，此操作不可撤销。`,
              okText: "确认截断并保存",
              okButtonProps: { danger: true },
              cancelText: "取消",
              onOk: () => handleSubmit(true),
            });
            return;
          }
          throw err;
        }
        message.success("科目信息已更新");
      } else {
        const create: CreateSubject = {
          code: values.code,
          name: values.name,
          fullScore: values.fullScore,
          scoreBandEdges: values.scoreBandEdges ?? null,
        };
        await subjectsApi.createSubject(create);
        message.success("科目已创建");
      }
      setDialogOpen(false);
      void fetchList();
    } catch (err) {
      if (err instanceof ApiError) message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (id: string) => {
    try {
      await subjectsApi.disableSubject(id);
      message.success("科目已停用");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const handleEnable = async (id: string) => {
    try {
      await subjectsApi.enableSubject(id);
      message.success("科目已启用");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  /** 查看关联 SKU：跳转 SKU 列表并按科目筛选。 */
  const viewRelatedSkus = (record: SubjectItem) => {
    navigate(`/catalog/skus?subjectId=${record.id}`);
  };

  // 行操作统一 EDIT 闸控（对齐后端 @Roles）；无权限整列不渲染，越权由后端 403 兜底
  const canManage = useHasPermission("ADM-27", "EDIT");
  const { columns, components } = useResizableColumns<SubjectItem>([
    { title: "科目编码", dataIndex: "code", width: 140, ellipsis: true },
    { title: "科目名称", dataIndex: "name", width: 180, ellipsis: true },
    {
      title: "满分",
      dataIndex: "fullScore",
      width: 90,
      align: "center",
      render: (v: number) => `${v} 分`,
    },
    { title: "关联SKU数", dataIndex: "skuCount", width: 100, align: "center" },
    {
      title: "状态",
      dataIndex: "status",
      width: 100,
      render: (status: SubjectStatus) => <SubjectStatusTag status={status} />,
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 180,
      render: (v: string) => (v ? new Date(v).toLocaleString("zh-CN") : "-"),
    },
    ...(canManage
      ? ([
          {
            title: "操作",
            fixed: "right",
            key: "action",
            width: 240,
            render: (_, record) => (
              <Space size="small">
                <Button
                  type="link"
                  size="small"
                  onClick={() => openEdit(record)}
                >
                  编辑
                </Button>
                {record.status === "ACTIVE" ? (
                  <Popconfirm
                    title="确定停用该科目吗？"
                    description="停用后不进入新建 SKU 的科目选择器"
                    onConfirm={() => handleDisable(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>
                      停用
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="确定启用该科目吗？"
                    onConfirm={() => handleEnable(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small">
                      启用
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  type="link"
                  size="small"
                  onClick={() => viewRelatedSkus(record)}
                >
                  查看关联SKU
                </Button>
              </Space>
            ),
          },
        ] as ColumnsType<SubjectItem>)
      : []),
  ]);

  return (
    <div>
      <Title level={4}>科目管理</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="科目名称"
            allowClear
            value={filters.name ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, name: e.target.value || undefined }))
            }
            style={{ width: 180 }}
          />
          <Input
            placeholder="科目编码"
            allowClear
            value={filters.code ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, code: e.target.value || undefined }))
            }
            style={{ width: 140 }}
          />
          <Select
            placeholder="状态"
            allowClear
            value={filters.status}
            onChange={(val) =>
              setFilters((f) => ({ ...f, status: val ?? undefined }))
            }
            style={{ width: 120 }}
            options={[
              { value: "ACTIVE", label: "正常" },
              { value: "DISABLED", label: "已停用" },
            ]}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => void fetchList()}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 无 ADM-27 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
          <HasPermission menuKey="ADM-27" action="CREATE">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新建科目
            </Button>
          </HasPermission>
        </div>
        <Table<SubjectItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>
      <EditDialog
        title={editing ? "编辑科目" : "新建科目"}
        open={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={submitting}
        okText="保存"
        cancelText="取消"
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ fullScore: 100 }}>
          <Form.Item
            name="code"
            label="科目编码"
            rules={[
              { required: true, message: "请输入科目编码" },
              { max: 50, message: "编码不能超过 50 个字符" },
            ]}
          >
            <Input placeholder="如 MATH" disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="name"
            label="科目名称"
            rules={[{ required: true, message: "请输入科目名称" }]}
          >
            <Input placeholder="如 数学" />
          </Form.Item>
          <Form.Item
            name="fullScore"
            label="满分（总分）"
            rules={[
              { required: true, message: "请输入科目满分" },
              {
                type: "integer",
                min: 1,
                max: 1000,
                message: "科目满分必须为 1-1000 的整数",
              },
            ]}
            extra="不同科目总分不同（如 60/100/120/150）；调低满分时若存在超出新满分的分数记录将二次确认截断"
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="如 60 / 100 / 120 / 150"
            />
          </Form.Item>
          <Form.Item
            name="scoreBandEdges"
            label="分数段"
            extra="默认按满分 60%/70%/80%/90% 比例派生；筛选分班下拉与快捷选择使用此处派生结果。修改满分保存后分数段将按新满分重新派生"
            rules={[
              {
                validator: (_, value: Array<number | null> | null) => {
                  if (!Array.isArray(value)) return Promise.resolve();
                  const fullScore =
                    watchedFullScore ?? editing?.fullScore ?? 100;
                  for (let i = 0; i < value.length; i++) {
                    const edge = value[i];
                    if (
                      edge === null ||
                      edge === undefined ||
                      !Number.isInteger(edge) ||
                      edge < 1 ||
                      edge > fullScore - 1
                    ) {
                      return Promise.reject(
                        new Error(
                          `边界点须为 1-${fullScore - 1} 的整数（第 ${i + 1} 个非法）`,
                        ),
                      );
                    }
                    const prev = value[i - 1];
                    if (prev !== undefined && prev !== null && edge <= prev) {
                      return Promise.reject(
                        new Error(`边界点须严格升序（${prev} 后出现 ${edge}）`),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ScoreBandEditor
              fullScore={watchedFullScore ?? editing?.fullScore ?? 100}
              derivedScoreBands={editing?.derivedScoreBands ?? []}
            />
          </Form.Item>
        </Form>
      </EditDialog>
    </div>
  );
}
